/**
 * The safe Markdown subset a bot's chat reply is drawn with (Channels C0b, AB#20876, design section 3.1 "agents-markdown").
 *
 * A reply is written by a model that read tool output, so its text is untrusted. This module only turns it into plain
 * data (blocks of inline nodes); {@link AgentsMarkdownComponent} draws that data with Angular bindings. Nothing is ever
 * handed to the browser as HTML: raw HTML is shown as the characters it is, and a link keeps its target only when
 * {@link safeLinkHref} accepts it (https, or a same-origin path starting with one '/'). Any other link is shown as the
 * Markdown it was written as, so the reader still sees where it pointed.
 *
 * The subset: paragraphs (every newline is a line break, as in a chat), headings (drawn as h3 to h5), bold, italic,
 * inline code, fenced code (the language is a text label only), ordered and unordered lists nested one level, block
 * quotes, GitHub pipe tables, horizontal rules, links, <https://...> autolinks and bare https URLs. It is not CommonMark
 * and does not try to be.
 *
 * Every step is linear in the length of the text: block readers use anchored patterns that cannot backtrack, brackets
 * and code spans are paired in one pass, emphasis uses the CommonMark delimiter stack with a per-character floor and
 * builds its tree in constant time per delimiter however deep it nests, a bare URL candidate that is refused is kept as
 * text and never rescanned, and the few bounded look-aheads (a link's address, an autolink) stop at a fixed length. The
 * text is capped at {@link AGENTS_MARKDOWN_MAX_CHARS} characters before anything is read.
 *
 * The output is bounded by the text too: a table row draws only the cells it has, and tables are capped per table
 * ({@link AGENTS_MARKDOWN_MAX_TABLE_ROWS} rows, {@link AGENTS_MARKDOWN_MAX_TABLE_CELLS} cells) and per text
 * ({@link AGENTS_MARKDOWN_MAX_DOCUMENT_TABLE_CELLS} cells), with the lines past a cap shown as one plain-text block
 * (drawn like an unlabelled code block: one text node, exactly as written).
 */

/** One inline node. A link's children never contain another link. */
export type MdInline =
  | { kind: 'text'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'br' }
  | { kind: 'strong'; children: MdInline[] }
  | { kind: 'em'; children: MdInline[] }
  | { kind: 'link'; href: string; children: MdInline[] };

export interface MdListItem {
  inline: MdInline[];
  /** The lists nested under this item (one level only: a nested item never has its own). */
  children: MdList[];
}

export interface MdList {
  ordered: boolean;
  /** The first number of an ordered list (null for a bullet list, or a list that starts at 1). */
  start: number | null;
  items: MdListItem[];
}

export type MdTableAlign = 'left' | 'center' | 'right' | null;

export type MdBlock =
  | { kind: 'heading'; level: 3 | 4 | 5; inline: MdInline[] }
  | { kind: 'paragraph'; inline: MdInline[] }
  | { kind: 'code'; lang: string; text: string }
  | ({ kind: 'list' } & MdList)
  | { kind: 'quote'; blocks: MdBlock[] }
  | { kind: 'rule' }
  | { kind: 'table'; align: MdTableAlign[]; header: MdInline[][]; rows: MdInline[][][] };

export interface MdDocument {
  blocks: MdBlock[];
  /** True when the text was longer than the cap and only its start was read. */
  truncated: boolean;
}

/** The longest text read. Anything longer is cut (and the cut is shown), never parsed slowly. */
export const AGENTS_MARKDOWN_MAX_CHARS = 100_000;

const MAX_HREF_CHARS = 2048;
const MAX_LINK_TEXT_CHARS = 1000;
const MAX_TITLE_CHARS = 500;
const MAX_LANG_CHARS = 40;
const MAX_TABLE_COLUMNS = 50;
/**
 * A table draws at most this many cells (header included) and this many body rows; the table lines beyond either cap
 * are shown as one block of plain text, exactly as written. A row draws only the cells it has (never padded to the
 * header), so a line of one '|' is one cell, not fifty.
 */
export const AGENTS_MARKDOWN_MAX_TABLE_CELLS = 5_000;
export const AGENTS_MARKDOWN_MAX_TABLE_ROWS = 500;
/** All the tables of one text together draw at most this many cells; later table lines are shown as plain text. */
export const AGENTS_MARKDOWN_MAX_DOCUMENT_TABLE_CELLS = 10_000;
/** Emphasis deeper than this is shown as its delimiter characters (keeps the rendered tree shallow). */
const MAX_EMPHASIS_DEPTH = 6;

// ---------------------------------------------------------------------------------------------------------------------
// Links
// ---------------------------------------------------------------------------------------------------------------------

/**
 * The link target to keep, or null to show the link as text.
 *
 * Kept: an absolute https URL with a host and no user name or password, written with 'https://' (any case), and a
 * same-origin path that starts with exactly one '/'. Refused: every other scheme (javascript:, data:, vbscript:, http:,
 * file:, mailto:), a protocol-relative '//host', a backslash anywhere (browsers read '/\host' as '//host'), and any
 * whitespace or control character anywhere (so ' javascript:' and 'java\tscript:' cannot pass as something else).
 * Character references are never decoded, so '&#106;avascript:' is just a string that does not start with https.
 */
export function safeLinkHref(raw: string | null | undefined): string | null {
  if (!raw || raw.length > MAX_HREF_CHARS) return null;
  // Any whitespace (Unicode included), C0/C1 control, backslash or angle bracket refuses the link outright.
  // eslint-disable-next-line no-control-regex
  if (/[\s\u0000-\u001F\u007F-\u009F\\<>]/.test(raw)) return null;

  if (raw[0] === '/') {
    return raw[1] === '/' ? null : raw;
  }

  if (!/^https:\/\//i.test(raw)) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' || !url.hostname || url.username || url.password) return null;
  return url.href;
}

// ---------------------------------------------------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------------------------------------------------

/** The blocks of a Markdown text, and whether it was cut. */
export function parseAgentsMarkdown(source: string | null | undefined): MdDocument {
  let text = typeof source === 'string' ? source : '';
  let truncated = false;
  if (text.length > AGENTS_MARKDOWN_MAX_CHARS) {
    let end = AGENTS_MARKDOWN_MAX_CHARS;
    // Never cut a surrogate pair in half.
    const last = text.charCodeAt(end - 1);
    if (last >= 0xd800 && last <= 0xdbff) end--;
    text = text.slice(0, end);
    truncated = true;
  }
  text = text.replace(/\r\n?/g, '\n').replaceAll('\u0000', '�');
  const budget: TableBudget = { cells: AGENTS_MARKDOWN_MAX_DOCUMENT_TABLE_CELLS };
  return { blocks: parseBlocks(text.split('\n'), true, budget), truncated };
}

/** The table cells the rest of the text may still draw (shared by a quote and the text around it). */
interface TableBudget {
  cells: number;
}

function parseBlocks(lines: string[], allowQuote: boolean, budget: TableBudget): MdBlock[] {
  const blocks: MdBlock[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (isBlank(line)) {
      i++;
      continue;
    }

    const fence = readFence(line);
    if (fence) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !isClosingFence(lines[i], fence.marker)) {
        body.push(stripIndent(lines[i], fence.indent));
        i++;
      }
      i++; // the closing fence, or the end of the text
      blocks.push({ kind: 'code', lang: fence.lang, text: body.join('\n') });
      continue;
    }

    const heading = readHeading(line);
    if (heading) {
      blocks.push({ kind: 'heading', level: heading.level, inline: parseInline(heading.text) });
      i++;
      continue;
    }

    if (isRule(line)) {
      blocks.push({ kind: 'rule' });
      i++;
      continue;
    }

    if (allowQuote && isQuote(line)) {
      const quoted: string[] = [];
      while (i < lines.length && isQuote(lines[i])) {
        quoted.push(lines[i].replace(/^ {0,3}> ?/, ''));
        i++;
      }
      // One level: a '>' inside a quote is text.
      blocks.push({ kind: 'quote', blocks: parseBlocks(quoted, false, budget) });
      continue;
    }

    if (readListMarker(line)) {
      const { lists, next } = readLists(lines, i);
      for (const list of lists) blocks.push({ kind: 'list', ...list });
      i = Math.max(next, i + 1); // always move on
      continue;
    }

    if (isTableStart(lines, i)) {
      const { tableBlocks, next } = readTable(lines, i, budget);
      blocks.push(...tableBlocks);
      i = next;
      continue;
    }

    const paragraph: string[] = [line.trim()];
    i++;
    while (i < lines.length && !isBlank(lines[i]) && !startsBlock(lines, i, allowQuote)) {
      paragraph.push(lines[i].trim());
      i++;
    }
    blocks.push({ kind: 'paragraph', inline: parseInline(paragraph.join('\n')) });
  }

  return blocks;
}

function isBlank(line: string): boolean {
  return line.trim().length === 0;
}

function isQuote(line: string): boolean {
  return /^ {0,3}>/.test(line);
}

/** Removes up to `count` leading spaces (a fence opened inside a list keeps its body's relative indentation). */
function stripIndent(line: string, count: number): string {
  let k = 0;
  while (k < count && line[k] === ' ') k++;
  return line.slice(k);
}

/** An opening code fence (any indentation, so one under a list item still opens), or null. */
function readFence(line: string): { marker: string; lang: string; indent: number } | null {
  const open = /^( *)(`{3,}|~{3,})/.exec(line);
  if (!open) return null;
  const info = line.slice(open[0].length).trim();
  // A backtick fence's info cannot hold a backtick (that line is inline code).
  if (open[2][0] === '`' && info.includes('`')) return null;
  const space = info.search(/\s/);
  const lang = (space < 0 ? info : info.slice(0, space)).slice(0, MAX_LANG_CHARS);
  return { marker: open[2], lang, indent: open[1].length };
}

/** Whether a line closes a fence: the same character, at least as many, then nothing but spaces. */
function isClosingFence(line: string, marker: string): boolean {
  const close = /^ *(`{3,}|~{3,})/.exec(line);
  return !!close && close[1][0] === marker[0] && close[1].length >= marker.length && line.slice(close[0].length).trim() === '';
}

/** An ATX heading, drawn one to three levels lower (h3 to h5) so a reply never shouts over the page around it. */
function readHeading(line: string): { level: 3 | 4 | 5; text: string } | null {
  const open = /^ {0,3}(#{1,6})(?:[ \t]+|$)/.exec(line);
  if (!open) return null;
  let text = line.slice(open[0].length).trimEnd();
  // The closing '#'s are removed by hand ('(.*?)\s*#*\s*$' backtracks badly on long space runs).
  let end = text.length;
  while (end > 0 && text[end - 1] === '#') end--;
  if (end < text.length && (end === 0 || text[end - 1] === ' ' || text[end - 1] === '\t')) text = text.slice(0, end).trimEnd();
  const level = open[1].length === 1 ? 3 : open[1].length === 2 ? 4 : 5;
  return { level, text };
}

/** Three or more of the same '-', '*' or '_', with only spaces and tabs between them. Checked by hand. */
function isRule(line: string): boolean {
  let marker = '';
  let count = 0;
  let leading = 0;
  for (let k = 0; k < line.length; k++) {
    const c = line[k];
    if (c === ' ' || c === '\t') {
      if (count === 0 && ++leading > 3) return false;
      continue;
    }
    if (c !== '-' && c !== '*' && c !== '_') return false;
    if (marker && c !== marker) return false;
    marker = c;
    count++;
  }
  return count >= 3;
}

interface ListMarker {
  indent: number;
  ordered: boolean;
  number: number;
  text: string;
}

/** A list item's marker and text, or null. The marker must be followed by a space or tab and some text. */
function readListMarker(line: string): ListMarker | null {
  const item = /^([ \t]*)([-*+]|(\d{1,9})[.)])[ \t]+(?=\S)/.exec(line);
  if (!item) return null;
  return {
    indent: item[1].replace(/\t/g, '    ').length,
    ordered: item[3] !== undefined,
    number: Number(item[3] ?? 1),
    text: line.slice(item[0].length).trimEnd()
  };
}

/**
 * The lists starting at line `start`. An item indented at least two columns more than the first item is nested (any
 * deeper indentation is still one level). A change between bullets and numbers at the same level starts a new list.
 */
function readLists(lines: string[], start: number): { lists: MdList[]; next: number } {
  const lists: MdList[] = [];
  const base = readListMarker(lines[start])!.indent;
  let top: MdList | null = null;
  let lastItem: MdListItem | null = null;
  let lastTexts: string[] | null = null;
  const pending: Array<{ item: MdListItem; texts: string[] }> = [];
  let i = start;

  const newList = (marker: ListMarker): MdList => ({
    ordered: marker.ordered,
    start: marker.ordered && marker.number !== 1 ? marker.number : null,
    items: []
  });

  while (i < lines.length) {
    const line = lines[i];
    const marker = readListMarker(line);
    if (marker && !isRule(line)) {
      const nested = marker.indent >= base + 2 && top !== null && top.items.length > 0;
      const item: MdListItem = { inline: [], children: [] };
      const texts = [marker.text];
      if (nested) {
        const parent = top!.items[top!.items.length - 1];
        let sub = parent.children[parent.children.length - 1];
        if (!sub || sub.ordered !== marker.ordered) {
          sub = newList(marker);
          parent.children.push(sub);
        }
        sub.items.push(item);
      } else {
        if (!top || top.ordered !== marker.ordered) {
          top = newList(marker);
          lists.push(top);
        }
        top.items.push(item);
      }
      pending.push({ item, texts });
      lastItem = item;
      lastTexts = texts;
      i++;
      continue;
    }
    if (isBlank(line)) {
      // A blank line followed by another item keeps the list going.
      if (i + 1 < lines.length && readListMarker(lines[i + 1]) && !isRule(lines[i + 1])) {
        i++;
        continue;
      }
      break;
    }
    // An indented line that is not a new block continues the item above it (as a line break).
    if (lastItem && lastTexts && /^[ \t]+\S/.test(line) && !readFence(line) && !isQuote(line) && !readHeading(line)) {
      lastTexts.push(line.trim());
      i++;
      continue;
    }
    break;
  }

  for (const { item, texts } of pending) item.inline = parseInline(texts.join('\n'));
  return { lists, next: i };
}

/** Splits a table row on unescaped '|'; '\|' is a literal bar in the cell (inside code too, as on GitHub). */
function splitRow(line: string): string[] {
  let row = line.trim();
  if (row.startsWith('|')) row = row.slice(1);
  if (row.endsWith('|') && !row.endsWith('\\|')) row = row.slice(0, -1);
  const cells: string[] = [];
  let current = '';
  for (let k = 0; k < row.length; k++) {
    if (row[k] === '\\' && row[k + 1] === '|') {
      current += '|';
      k++;
    } else if (row[k] === '|') {
      cells.push(current.trim());
      current = '';
    } else {
      current += row[k];
    }
  }
  cells.push(current.trim());
  return cells;
}

/** A table's divider row: as many cells as the header, each dashes with optional alignment colons. */
function isTableDivider(line: string, columns: number): boolean {
  if (!line.includes('-')) return false;
  const cells = splitRow(line);
  return cells.length === columns && cells.every(cell => /^:?-+:?$/.test(cell));
}

function isTableStart(lines: string[], i: number): boolean {
  return lines[i].includes('|') && i + 1 < lines.length && lines[i + 1].includes('-')
    && isTableDivider(lines[i + 1], splitRow(lines[i]).length);
}

/**
 * The table starting at line `start`, then (when it reached a cap) its remaining lines as one plain-text block.
 *
 * Work and output stay proportional to the text: a body row keeps only the cells it has (at most the header's width),
 * the table stops at {@link AGENTS_MARKDOWN_MAX_TABLE_ROWS} rows or {@link AGENTS_MARKDOWN_MAX_TABLE_CELLS} cells, and
 * all the text's tables share {@link AGENTS_MARKDOWN_MAX_DOCUMENT_TABLE_CELLS}. A table whose header alone does not fit
 * the budget left is shown whole as plain text.
 */
function readTable(lines: string[], start: number, budget: TableBudget): { tableBlocks: MdBlock[]; next: number } {
  const isTableLine = (line: string): boolean => !isBlank(line) && line.includes('|');
  const header = splitRow(lines[start]).slice(0, MAX_TABLE_COLUMNS);
  const cellCap = Math.min(AGENTS_MARKDOWN_MAX_TABLE_CELLS, budget.cells);
  const tableBlocks: MdBlock[] = [];
  let i = start;

  if (header.length <= cellCap) {
    const divider = splitRow(lines[start + 1]);
    const align: MdTableAlign[] = header.map((_, column) => {
      const cell = divider[column] ?? '';
      const left = cell.startsWith(':');
      const right = cell.endsWith(':');
      return left && right ? 'center' : right ? 'right' : left ? 'left' : null;
    });
    const rows: MdInline[][][] = [];
    let cells = header.length;
    i = start + 2;
    while (i < lines.length && isTableLine(lines[i]) && rows.length < AGENTS_MARKDOWN_MAX_TABLE_ROWS) {
      const row = splitRow(lines[i]).slice(0, header.length);
      if (cells + row.length > cellCap) break;
      cells += row.length;
      rows.push(row.map(cell => parseInline(cell)));
      i++;
    }
    budget.cells -= cells;
    tableBlocks.push({ kind: 'table', align, header: header.map(cell => parseInline(cell)), rows });
  }

  // The table lines past a cap: one block of plain text exactly as written (a single text node, with no cells and no
  // inline parsing), so whatever follows a cap costs one node to draw however many lines it has.
  const rest = i;
  while (i < lines.length && isTableLine(lines[i])) i++;
  if (i > rest) tableBlocks.push({ kind: 'code', lang: '', text: lines.slice(rest, i).join('\n') });
  return { tableBlocks, next: i };
}

/** Whether line i begins a block other than a paragraph (so a paragraph ends before it). */
function startsBlock(lines: string[], i: number, allowQuote: boolean): boolean {
  const line = lines[i];
  return readFence(line) !== null
    || readHeading(line) !== null
    || isRule(line)
    || (allowQuote && isQuote(line))
    || readListMarker(line) !== null
    || isTableStart(lines, i);
}

// ---------------------------------------------------------------------------------------------------------------------
// Inline
// ---------------------------------------------------------------------------------------------------------------------

type Token =
  | { kind: 'node'; node: MdInline }
  | { kind: 'text'; text: string }
  | {
      kind: 'delim';
      ch: '*' | '_';
      count: number;
      canOpen: boolean;
      canClose: boolean;
      /** Emphasis this run closes, innermost first. */
      closes: Array<'strong' | 'em'>;
      /** Emphasis this run opens, innermost first (pairs are found inside out; read it backwards for outermost first). */
      opens: Array<'strong' | 'em'>;
    };

/** '<scheme:...>', matched in place (sticky, no copy); the run stops at a space or angle bracket, so it stays linear. */
const AUTOLINK = /<([A-Za-z][A-Za-z0-9+.-]{1,31}:[^\s<>]{1,2048})>/y;
const ASCII_PUNCTUATION = '!"#$%&\'()*+,-./:;<=>?@[\\]^_`{|}~';
/** Characters a bare URL does not end with (the sentence's punctuation). */
const URL_TRAILING = '.,;:!?\'"*_~';

/** Whether 'https://' (any case) starts at `at`. */
function startsBareUrl(source: string, at: number): boolean {
  return source.slice(at, at + 8).toLowerCase() === 'https://';
}

function isWhitespace(c: string | undefined): boolean {
  return c === undefined || /\s/.test(c);
}

function isPunctuation(c: string | undefined): boolean {
  return c !== undefined && /[\p{P}\p{S}]/u.test(c);
}

/** The inline nodes of one paragraph (lines joined with '\n', each drawn as a line break). */
export function parseInline(source: string): MdInline[] {
  return buildTree(tokenize(source, true));
}

/**
 * Pairs every code span in one pass: a backtick run opens a span that the next run of exactly the same length closes.
 * Returns, for each opening position, the position just after its closing run.
 */
function pairCodeSpans(source: string): Map<number, { end: number; contentStart: number; contentEnd: number }> {
  const runs: Array<{ at: number; length: number }> = [];
  for (let k = 0; k < source.length; k++) {
    if (source[k] !== '`') continue;
    const at = k;
    while (k + 1 < source.length && source[k + 1] === '`') k++;
    runs.push({ at, length: k - at + 1 });
  }
  // For each run, the index of the next run with the same length (one reverse pass).
  const nextSame = new Array<number>(runs.length).fill(-1);
  const seen = new Map<number, number>();
  for (let r = runs.length - 1; r >= 0; r--) {
    nextSame[r] = seen.get(runs[r].length) ?? -1;
    seen.set(runs[r].length, r);
  }
  const spans = new Map<number, { end: number; contentStart: number; contentEnd: number }>();
  let r = 0;
  while (r < runs.length) {
    const run = runs[r];
    if (isEscaped(source, run.at)) {
      // An escaped opener ('\`') is literal text. Escapes do not apply inside a span, so only the opener is checked.
      r++;
      continue;
    }
    const close = nextSame[r];
    if (close < 0) {
      r++;
      continue;
    }
    spans.set(run.at, {
      end: runs[close].at + runs[close].length,
      contentStart: run.at + run.length,
      contentEnd: runs[close].at
    });
    r = close + 1;
  }
  return spans;
}

/** Whether the character at `at` is preceded by an odd number of backslashes. */
function isEscaped(source: string, at: number): boolean {
  let slashes = 0;
  for (let k = at - 1; k >= 0 && source[k] === '\\'; k--) slashes++;
  return slashes % 2 === 1;
}

/** Pairs '[' with its matching ']' in one pass (escaped brackets and brackets inside code spans are skipped). */
function pairBrackets(source: string, codeSpans: Map<number, { end: number }>): Map<number, number> {
  const pairs = new Map<number, number>();
  const stack: number[] = [];
  let k = 0;
  while (k < source.length) {
    const c = source[k];
    if (c === '\\') {
      k += 2;
      continue;
    }
    const span = codeSpans.get(k);
    if (span) {
      k = span.end;
      continue;
    }
    if (c === '[') stack.push(k);
    else if (c === ']' && stack.length) pairs.set(stack.pop()!, k);
    k++;
  }
  return pairs;
}

const isBracket = (c: string | undefined): boolean => c === '[' || c === ']';
const isLinkSpace = (c: string | undefined): boolean => c === ' ' || c === '\t' || c === '\n';

/**
 * The destination and the position after ')' of a link whose '(' is at `open`, or null.
 *
 * The scan stops at the next '[' or ']' (a destination or title with a raw bracket is not read as a link). Every call
 * starts just after the ']' it was paired with, so the stretches different calls read never overlap and the total work
 * stays linear however many '[...](' a hostile text holds. It is also capped at a fixed length.
 */
function readDestination(source: string, open: number): { dest: string; end: number } | null {
  const limit = Math.min(source.length, open + 1 + MAX_HREF_CHARS + MAX_TITLE_CHARS + 16);
  let k = open + 1;
  while (k < limit && isLinkSpace(source[k])) k++;
  let dest = '';
  if (source[k] === '<') {
    let close = k + 1;
    while (close < limit && source[close] !== '>' && source[close] !== '<' && source[close] !== '\n' && !isBracket(source[close])) close++;
    if (source[close] !== '>' || close - k > MAX_HREF_CHARS) return null;
    dest = source.slice(k + 1, close);
    k = close + 1;
  } else {
    const from = k;
    let depth = 0;
    while (k < limit) {
      const c = source[k];
      if (c === '\\' && k + 1 < limit && !isBracket(source[k + 1])) {
        k += 2;
        continue;
      }
      if (isLinkSpace(c) || isBracket(c)) break;
      if (c === '(') depth++;
      if (c === ')') {
        if (depth === 0) break;
        depth--;
      }
      k++;
    }
    if (k - from > MAX_HREF_CHARS) return null;
    dest = source.slice(from, k);
  }
  while (k < limit && isLinkSpace(source[k])) k++;
  // An optional title ("...", '...' or (...)): read and ignored, never drawn.
  const quote = source[k];
  if (quote === '"' || quote === '\'' || quote === '(') {
    const closer = quote === '(' ? ')' : quote;
    let t = k + 1;
    while (t < limit && t - k <= MAX_TITLE_CHARS && source[t] !== closer && !isBracket(source[t])) {
      t += source[t] === '\\' && t + 1 < limit && !isBracket(source[t + 1]) ? 2 : 1;
    }
    if (source[t] !== closer) return null;
    k = t + 1;
    while (k < limit && isLinkSpace(source[k])) k++;
  }
  if (source[k] !== ')') return null;
  return { dest, end: k + 1 };
}

/** Removes the backslash from each escaped ASCII punctuation character (for a link's destination). */
function unescapeAscii(text: string): string {
  let out = '';
  for (let k = 0; k < text.length; k++) {
    if (text[k] === '\\' && k + 1 < text.length && ASCII_PUNCTUATION.includes(text[k + 1])) {
      out += text[k + 1];
      k++;
    } else {
      out += text[k];
    }
  }
  return out;
}

/** One pass over the text into tokens; emphasis runs are then paired by {@link resolveEmphasis}. */
function tokenize(source: string, allowLinks: boolean): Token[] {
  const tokens: Token[] = [];
  const codeSpans = pairCodeSpans(source);
  const brackets = allowLinks ? pairBrackets(source, codeSpans) : new Map<number, number>();
  let buffer = '';
  const flush = (): void => {
    if (buffer) tokens.push({ kind: 'text', text: buffer });
    buffer = '';
  };
  const node = (value: MdInline): void => {
    flush();
    tokens.push({ kind: 'node', node: value });
  };

  let i = 0;
  while (i < source.length) {
    const c = source[i];

    if (c === '\\') {
      const next = source[i + 1];
      if (next === '\n') {
        // A backslash at the end of a line is a hard break; every newline already is one.
        i++;
        continue;
      }
      if (next !== undefined && ASCII_PUNCTUATION.includes(next)) {
        buffer += next;
        i += 2;
        continue;
      }
    }

    if (c === '\n') {
      node({ kind: 'br' });
      i++;
      continue;
    }

    if (c === '`') {
      const span = codeSpans.get(i);
      if (span) {
        let code = source.slice(span.contentStart, span.contentEnd).replace(/\n/g, ' ');
        if (code.length > 1 && code.startsWith(' ') && code.endsWith(' ') && code.trim()) code = code.slice(1, -1);
        node({ kind: 'code', text: code });
        i = span.end;
        continue;
      }
      let k = i;
      while (k < source.length && source[k] === '`') k++;
      buffer += source.slice(i, k);
      i = k;
      continue;
    }

    if (c === '[' && allowLinks) {
      const close = brackets.get(i);
      if (close !== undefined && close - i - 1 <= MAX_LINK_TEXT_CHARS && source[close + 1] === '(') {
        const target = readDestination(source, close + 1);
        if (target) {
          const href = safeLinkHref(unescapeAscii(target.dest));
          if (href) {
            node({ kind: 'link', href, children: buildTree(tokenize(source.slice(i + 1, close), false)) });
          } else {
            // Not a link we draw: shown exactly as written, so the reader still sees where it pointed.
            buffer += source.slice(i, target.end);
          }
          i = target.end;
          continue;
        }
      }
    }

    if (c === '<' && allowLinks) {
      AUTOLINK.lastIndex = i;
      const autolink = AUTOLINK.exec(source);
      if (autolink) {
        const href = safeLinkHref(autolink[1]);
        if (href) node({ kind: 'link', href, children: [{ kind: 'text', text: autolink[1] }] });
        else buffer += autolink[0];
        i += autolink[0].length;
        continue;
      }
    }

    if ((c === 'h' || c === 'H') && allowLinks && (i === 0 || /[\s(]/.test(source[i - 1])) && startsBareUrl(source, i)) {
      let end = i + 8;
      // A candidate also stops at a '(' that starts another 'https://', so candidates never overlap.
      while (end < source.length && end - i <= MAX_HREF_CHARS && !/[\s<>]/.test(source[end])
        && !(source[end] === '(' && startsBareUrl(source, end + 1))) end++;
      // Trailing punctuation belongs to the sentence; a ')' stays only when it closes a '(' in the URL.
      let opens = 0;
      let closes = 0;
      for (let k = i; k < end; k++) {
        if (source[k] === '(') opens++;
        else if (source[k] === ')') closes++;
      }
      while (end > i + 8) {
        const last = source[end - 1];
        if (URL_TRAILING.includes(last)) {
          end--;
        } else if (last === ')' && closes > opens) {
          closes--;
          end--;
        } else {
          break;
        }
      }
      const url = source.slice(i, end);
      const href = end > i + 8 ? safeLinkHref(url) : null;
      if (href) {
        node({ kind: 'link', href, children: [{ kind: 'text', text: url }] });
      } else {
        // Refused (or only 'https://'): shown as written, and never scanned again from the next character.
        buffer += url;
      }
      i = end;
      continue;
    }

    if (c === '*' || c === '_') {
      let k = i;
      while (k < source.length && source[k] === c) k++;
      const before = i > 0 ? source[i - 1] : undefined;
      const after = k < source.length ? source[k] : undefined;
      const leftFlanking = !isWhitespace(after) && (!isPunctuation(after) || isWhitespace(before) || isPunctuation(before));
      const rightFlanking = !isWhitespace(before) && (!isPunctuation(before) || isWhitespace(after) || isPunctuation(after));
      const canOpen = c === '*' ? leftFlanking : leftFlanking && (!rightFlanking || isPunctuation(before));
      const canClose = c === '*' ? rightFlanking : rightFlanking && (!leftFlanking || isPunctuation(after));
      flush();
      tokens.push({ kind: 'delim', ch: c, count: k - i, canOpen, canClose, closes: [], opens: [] });
      i = k;
      continue;
    }

    buffer += c;
    i++;
  }
  flush();
  resolveEmphasis(tokens);
  return tokens;
}

/**
 * The CommonMark delimiter-stack pass (without the rule of three). Each closer looks back for the nearest opener of the
 * same character; delimiters between a matched pair are dropped from the stack, and a failed look-back raises that
 * character's floor, so every delimiter is visited a bounded number of times.
 */
function resolveEmphasis(tokens: Token[]): void {
  type Delim = Extract<Token, { kind: 'delim' }>;
  const stack: Delim[] = [];
  const floor: Record<'*' | '_', number> = { '*': 0, '_': 0 };

  for (const token of tokens) {
    if (token.kind !== 'delim') continue;
    if (token.canClose) {
      let k = stack.length - 1;
      while (token.count > 0 && k >= floor[token.ch]) {
        const opener = stack[k];
        if (opener.ch !== token.ch || opener.count === 0) {
          k--;
          continue;
        }
        const use = opener.count >= 2 && token.count >= 2 ? 2 : 1;
        const kind = use === 2 ? 'strong' : 'em';
        opener.count -= use;
        token.count -= use;
        opener.opens.push(kind);
        token.closes.push(kind);
        // Everything above the opener is now inside the pair and can no longer match outside it.
        stack.length = opener.count > 0 ? k + 1 : k;
        k = stack.length - 1;
      }
      for (const ch of ['*', '_'] as const) floor[ch] = Math.min(floor[ch], stack.length);
      if (token.count > 0) floor[token.ch] = stack.length;
    }
    if (token.canOpen && token.count > 0) stack.push(token);
  }
}

/**
 * The tree of inline nodes from resolved tokens. Emphasis deeper than the cap is shown as its characters.
 *
 * A flat frame (one past the cap) is only ever pushed while `depth` real frames sit below it, and a real frame is only
 * pushed while there are none, so the flat frames are always the top of the stack and `frames[depth - 1]` is the
 * innermost real frame. Finding where text goes is therefore constant time however deep the emphasis is.
 */
function buildTree(tokens: Token[]): MdInline[] {
  type Frame = { kind: 'strong' | 'em'; children: MdInline[]; flat: boolean; literal: string };
  const root: MdInline[] = [];
  const frames: Frame[] = [];
  /** How many real (drawn) frames are open; they are frames[0] to frames[depth - 1]. */
  let depth = 0;
  const target = (): MdInline[] => (depth > 0 ? frames[depth - 1].children : root);
  const text = (value: string): void => {
    if (!value) return;
    const into = target();
    const last = into[into.length - 1];
    if (last && last.kind === 'text') last.text += value;
    else into.push({ kind: 'text', text: value });
  };

  for (const token of tokens) {
    if (token.kind === 'text') {
      text(token.text);
    } else if (token.kind === 'node') {
      target().push(token.node);
    } else {
      for (const kind of token.closes) {
        const frame = frames.pop();
        if (!frame) {
          text(token.ch.repeat(kind === 'strong' ? 2 : 1));
          continue;
        }
        if (frame.flat) {
          text(frame.literal);
        } else {
          depth--;
          target().push({ kind: frame.kind, children: frame.children });
        }
      }
      text(token.ch.repeat(token.count));
      for (let o = token.opens.length - 1; o >= 0; o--) {
        const kind = token.opens[o];
        const literal = token.ch.repeat(kind === 'strong' ? 2 : 1);
        const flat = depth >= MAX_EMPHASIS_DEPTH;
        if (flat) text(literal);
        else depth++;
        frames.push({ kind, children: [], flat, literal });
      }
    }
  }
  // Unclosed frames cannot happen (every open has its close), but never lose text if they do.
  while (frames.length) {
    const frame = frames.pop()!;
    if (!frame.flat) target().push({ kind: frame.kind, children: frame.children });
  }
  return root;
}
